import React, { useState, useRef, useEffect } from "react";
import { X, Camera, Loader2, User, Check, Zap, Crown, Star, BarChart3, Lock, Eye, EyeOff, CheckCircle, AlertCircle, Palette, Building2, Phone, Mail, Upload, PhoneCall } from "lucide-react";
import UsageDashboard from "./UsageDashboard";
import PhoneNumbersManager from "./PhoneNumbersManager";
import { uploadUserAvatar, updateUser } from "../api";
import { fetchWithAuth, BASE_URL } from "../api/client";
import { PROPERTY_GALLERY_TEMPLATES } from "../config/propertyGalleryTemplates";

// Helper to fix over-encoded S3 URLs (e.g., %252540 → %40 → @)
const decodeS3Url = (url) => {
  if (!url || typeof url !== 'string') return url;

  // Keep decoding while URL contains encoded percent signs (%25)
  let decoded = url;
  let prev = '';
  while (decoded !== prev && decoded.includes('%25')) {
    prev = decoded;
    try {
      decoded = decodeURIComponent(decoded);
    } catch (e) {
      // If decoding fails, return what we have
      break;
    }
  }
  return decoded;
};

// Preset accent colors for branding
const ACCENT_COLOR_PRESETS = [
  { id: 'gold', name: 'Gold', color: '#d6b27f' },
  { id: 'rose-gold', name: 'Rose Gold', color: '#b76e79' },
  { id: 'platinum', name: 'Platinum', color: '#d4d4d8' },
  { id: 'emerald', name: 'Emerald', color: '#34d399' },
  { id: 'sapphire', name: 'Sapphire', color: '#38bdf8' },
  { id: 'ruby', name: 'Ruby', color: '#e11d48' },
  { id: 'navy', name: 'Navy', color: '#1e3a5f' },
  { id: 'black', name: 'Black', color: '#1e293b' },
];

const SUBSCRIPTION_TIERS = [
  {
    id: 'free',
    name: 'Free',
    icon: Star,
    price: '$0',
    period: '/month',
    credits: '10 cases',
    features: [
      '3,000 A.I credits per month',
      '9-hour processing time',
      'Case Management Dashboard',
      'P&L Tracker',
      'Executive Dashboard',
      'Case Assistant',
      'Notes',
      'Meeting Assistant'
    ],
    color: 'slate',
    highlight: false
  },
  {
    id: 'starter',
    name: 'Starter',
    icon: Zap,
    price: '$39.99',
    period: '/month',
    features: [
      '10,000 A.I credits per month',
      '6-hour processing time',
      'Priority support',
      'Case Management Dashboard',
      'P&L Tracker',
      'Executive Dashboard',
      'Team: 3 users',
      'Case Assistant',
      'Notes',
      'Meeting Assistant'
    ],
    color: 'sky',
    highlight: false
  },
  {
    id: 'pro',
    name: 'Pro',
    icon: Crown,
    price: '$79.99',
    period: '/month',
    features: [
      '20,000 A.I credits per month',
      '3-hour processing time',
      'Priority support',
      'Case Management Dashboard',
      'P&L Tracker',
      'Executive Dashboard',
      'Team: 10 users',
      'Case Assistant',
      'Notes',
      'Meeting Assistant'
    ],
    color: 'violet',
    highlight: true
  }
];

const AccountSettingsModal = ({
  isAccountSettingsOpen,
  setIsAccountSettingsOpen,
  userEmail,
  userMeta,
  handleLogout,
  onUpdateMeta,
  onUpgradePlan,
  onOpenBillingPortal,
  isPaymentProcessing,
  featureFlags = {}
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedTab, setSelectedTab] = useState('profile'); // 'profile', 'subscription', 'usage', or 'branding'
  const fileInputRef = useRef(null);
  const agentPhotoInputRef = useRef(null);
  const brokerageLogoInputRef = useRef(null);

  // Branding state - initialize from userMeta if available
  const [brandingSettings, setBrandingSettings] = useState({
    agentName: userMeta?.agentName || '',
    agentTitle: userMeta?.agentTitle || 'Real Estate Agent',
    agentPhone: userMeta?.agentPhone || '',
    agentEmail: userMeta?.agentEmail || userEmail || '',
    agentPhoto: userMeta?.agentPhoto || null,
    brokerageName: userMeta?.brokerageName || '',
    brokerageLogo: userMeta?.brokerageLogo || null,
    accentColor: userMeta?.accentColor || '#d6b27f',
    defaultTemplate: userMeta?.defaultTemplate || 'luxury-dark'
  });
  const [isSavingBranding, setIsSavingBranding] = useState(false);
  const [brandingSaved, setBrandingSaved] = useState(false);
  const [isUploadingAgentPhoto, setIsUploadingAgentPhoto] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [agentPhotoError, setAgentPhotoError] = useState(false);
  const [brokerageLogoError, setBrokerageLogoError] = useState(false);
  const [agentPhotoPreview, setAgentPhotoPreview] = useState(null);
  const [brokerageLogoPreview, setBrokerageLogoPreview] = useState(null);

  // Load branding settings from userMeta when modal opens or userMeta changes
  useEffect(() => {
    // Only update when modal is open to avoid unnecessary state updates
    if (!isAccountSettingsOpen) return;

    if (userMeta) {
      // Decode over-encoded S3 URLs (backend sometimes triple-encodes them)
      const fixedAgentPhoto = decodeS3Url(userMeta.agentPhoto);
      const fixedBrokerageLogo = decodeS3Url(userMeta.brokerageLogo);

      console.log('[AccountSettingsModal] Loading branding from userMeta:', {
        agentName: userMeta.agentName,
        agentPhoto: userMeta.agentPhoto,
        fixedAgentPhoto,
        brokerageLogo: userMeta.brokerageLogo,
        fixedBrokerageLogo
      });
      setBrandingSettings({
        agentName: userMeta.agentName || '',
        agentTitle: userMeta.agentTitle || 'Real Estate Agent',
        agentPhone: userMeta.agentPhone || '',
        agentEmail: userMeta.agentEmail || userEmail || '',
        agentPhoto: fixedAgentPhoto || null,
        brokerageName: userMeta.brokerageName || '',
        brokerageLogo: fixedBrokerageLogo || null,
        accentColor: userMeta.accentColor || '#d6b27f',
        defaultTemplate: userMeta.defaultTemplate || 'luxury-dark'
      });
      // Reset previews and error states when userMeta changes
      setAgentPhotoPreview(null);
      setBrokerageLogoPreview(null);
      setAgentPhotoError(false);
      setBrokerageLogoError(false);
    }
  }, [isAccountSettingsOpen, userMeta, userEmail]);

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const currentTier = (userMeta.membership || 'Free').toLowerCase();

  if (!isAccountSettingsOpen) return null;

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Basic validation
    if (!file.type.startsWith('image/')) {
      alert("Please upload an image file.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("File size should be less than 5MB.");
      return;
    }

    setIsUploading(true);
    try {
      // 1. Upload the image
      const uploadRes = await uploadUserAvatar(file);
      // API returns url, avatar_url, or imageUrl - use whichever is available
      const newAvatarUrl = uploadRes.data.url || uploadRes.data.imageUrl || uploadRes.data.avatar_url;

      // 2. Update the user profile with the new URL
      await updateUser({ avatar_url: newAvatarUrl });

      // 3. Optional: Trigger a refresh of user data in parent
      if (onUpdateMeta) {
        onUpdateMeta({ ...userMeta, avatar_url: newAvatarUrl });
      }

    } catch (error) {
      console.error("Avatar upload failed:", error);
      alert("Failed to update avatar. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpgradeTier = (tierId) => {
    if (tierId === 'free') return; // Can't "upgrade" to free
    if (tierId === currentTier) return; // Already on this tier

    // Call the upgrade function with tier info
    onUpgradePlan(tierId);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError(null);

    // Validation
    if (!passwordData.currentPassword) {
      setPasswordError('Please enter your current password');
      return;
    }

    if (!passwordData.newPassword) {
      setPasswordError('Please enter a new password');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setIsChangingPassword(true);

    try {
      const response = await fetchWithAuth(`${BASE_URL}/api/user/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to change password');
      }

      // Success
      setPasswordSuccess(true);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);

      // Hide success message after 3 seconds
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const resetPasswordForm = () => {
    setShowPasswordForm(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError(null);
  };

  // Handle agent photo upload
  const handleAgentPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert("Please upload an image file.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("File size should be less than 5MB.");
      return;
    }

    // Create local preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setAgentPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);

    setIsUploadingAgentPhoto(true);
    setAgentPhotoError(false);
    try {
      const uploadRes = await uploadUserAvatar(file);
      // API returns url, avatar_url, or imageUrl - use whichever is available
      const photoUrl = uploadRes.data.url || uploadRes.data.imageUrl || uploadRes.data.avatar_url;
      setBrandingSettings(prev => ({ ...prev, agentPhoto: photoUrl }));
    } catch (error) {
      console.error("Agent photo upload failed:", error);
      alert("Failed to upload photo. Please try again.");
      setAgentPhotoPreview(null);
    } finally {
      setIsUploadingAgentPhoto(false);
    }
  };

  // Handle brokerage logo upload
  const handleBrokerageLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert("Please upload an image file.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("File size should be less than 5MB.");
      return;
    }

    // Create local preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setBrokerageLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);

    setIsUploadingLogo(true);
    setBrokerageLogoError(false);
    try {
      const uploadRes = await uploadUserAvatar(file);
      // API returns url, avatar_url, or imageUrl - use whichever is available
      const logoUrl = uploadRes.data.url || uploadRes.data.imageUrl || uploadRes.data.avatar_url;
      setBrandingSettings(prev => ({ ...prev, brokerageLogo: logoUrl }));
    } catch (error) {
      console.error("Logo upload failed:", error);
      alert("Failed to upload logo. Please try again.");
      setBrokerageLogoPreview(null);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Save branding settings
  const handleSaveBranding = async () => {
    setIsSavingBranding(true);
    const payload = {
      agentName: brandingSettings.agentName,
      agentTitle: brandingSettings.agentTitle,
      agentPhone: brandingSettings.agentPhone,
      agentEmail: brandingSettings.agentEmail,
      agentPhoto: brandingSettings.agentPhoto,
      brokerageName: brandingSettings.brokerageName,
      brokerageLogo: brandingSettings.brokerageLogo,
      accentColor: brandingSettings.accentColor,
      defaultTemplate: brandingSettings.defaultTemplate
    };

    console.log('[handleSaveBranding] Saving branding payload:', payload);

    try {
      const response = await updateUser(payload);
      console.log('[handleSaveBranding] Save response:', response);

      if (onUpdateMeta) {
        onUpdateMeta({ ...userMeta, ...brandingSettings });
      }

      setBrandingSaved(true);
      setTimeout(() => setBrandingSaved(false), 3000);
    } catch (error) {
      console.error("[handleSaveBranding] Failed to save branding:", error);
      alert("Failed to save branding settings. Please try again.");
    } finally {
      setIsSavingBranding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4 dark:bg-slate-950/80 bg-slate-500/50 backdrop-blur-sm">
      <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-5xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b dark:border-slate-800 border-slate-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                Account
              </p>
              <p className="text-lg font-bold dark:text-white text-slate-900">Account Settings</p>
              <p className="text-sm dark:text-slate-400 text-slate-500">
                Manage your profile, subscription, and billing
              </p>
            </div>
            <button
              onClick={() => setIsAccountSettingsOpen(false)}
              className="p-2 rounded-full text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 border-b dark:border-slate-800 border-slate-200 -mb-px">
            <button
              onClick={() => setSelectedTab('profile')}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${selectedTab === 'profile'
                ? 'text-sky-600 dark:text-sky-400'
                : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
            >
              Profile
              {selectedTab === 'profile' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 dark:bg-sky-400"></div>
              )}
            </button>
            <button
              onClick={() => setSelectedTab('subscription')}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${selectedTab === 'subscription'
                ? 'text-sky-600 dark:text-sky-400'
                : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
            >
              Subscription
              {selectedTab === 'subscription' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 dark:bg-sky-400"></div>
              )}
            </button>
            <button
              onClick={() => setSelectedTab('usage')}
              className={`px-4 py-2 text-sm font-medium transition-colors relative flex items-center gap-1.5 ${selectedTab === 'usage'
                ? 'text-sky-600 dark:text-sky-400'
                : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
            >
              <BarChart3 className="w-4 h-4" />
              Usage
              {selectedTab === 'usage' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 dark:bg-sky-400"></div>
              )}
            </button>
            <button
              onClick={() => setSelectedTab('branding')}
              className={`px-4 py-2 text-sm font-medium transition-colors relative flex items-center gap-1.5 ${selectedTab === 'branding'
                ? 'text-sky-600 dark:text-sky-400'
                : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
            >
              <Palette className="w-4 h-4" />
              Branding
              {selectedTab === 'branding' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 dark:bg-sky-400"></div>
              )}
            </button>
            {/* Phone Numbers tab - Ultra+ only */}
            {featureFlags.isUltraOrEnterprise && (
              <button
                onClick={() => setSelectedTab('phone')}
                className={`px-4 py-2 text-sm font-medium transition-colors relative flex items-center gap-1.5 ${selectedTab === 'phone'
                  ? 'text-sky-600 dark:text-sky-400'
                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                  }`}
              >
                <PhoneCall className="w-4 h-4" />
                Phone
                {selectedTab === 'phone' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 dark:bg-sky-400"></div>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {selectedTab === 'usage' ? (
            /* Usage Tab */
            <UsageDashboard userMeta={userMeta} isAdmin={false} />
          ) : selectedTab === 'profile' ? (
            /* Profile Tab */
            <div className="max-w-2xl mx-auto">
              <div className="space-y-4 dark:bg-slate-950/60 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-4">
                  <div className="relative group">
                    <div className="h-16 w-16 rounded-2xl dark:bg-slate-800 bg-slate-200 border-2 dark:border-slate-700 border-slate-300 overflow-hidden flex items-center justify-center">
                      {isUploading ? (
                        <Loader2 className="w-6 h-6 text-sky-500 animate-spin" />
                      ) : userMeta.avatar_url ? (
                        <img src={userMeta.avatar_url} alt="Profile" className="h-full w-full object-cover" />
                      ) : (
                        <User className="w-8 h-8 dark:text-slate-500 text-slate-400" />
                      )}
                    </div>
                    <button
                      onClick={handleAvatarClick}
                      disabled={isUploading}
                      className="absolute -bottom-1 -right-1 p-1.5 rounded-lg bg-sky-600 text-white shadow-lg hover:bg-sky-500 transition-colors disabled:opacity-50"
                    >
                      <Camera size={14} />
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="hidden"
                      accept="image/*"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold dark:text-white text-slate-900">Profile Picture</p>
                    <p className="text-xs text-slate-500">JPG, PNG or GIF. Max 5MB.</p>
                  </div>
                </div>

                <div className="space-y-2 text-sm dark:text-slate-200 text-slate-700 border-t dark:border-slate-800 border-slate-200 pt-4">
                  {(userMeta.firstName || userMeta.lastName) && (
                    <div className="flex justify-between">
                      <span>Name</span>
                      <span className="text-slate-500">
                        {[userMeta.firstName, userMeta.lastName].filter(Boolean).join(' ') || "Unknown"}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Email</span>
                    <span className="text-slate-500">
                      {userEmail || userMeta.email || "Unknown"}
                    </span>
                  </div>
                  {userMeta.company && (
                    <div className="flex justify-between">
                      <span>Company</span>
                      <span className="text-slate-500">{userMeta.company}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Membership</span>
                    <span className="text-slate-500">
                      {userMeta.membership || "Free"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Credits</span>
                    <span className="text-slate-500">{userMeta.credit ?? 0}</span>
                  </div>
                </div>

                {/* Password Change Section */}
                <div className="border-t dark:border-slate-800 border-slate-200 pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-medium dark:text-slate-200 text-slate-700">Password</span>
                    </div>
                    {!showPasswordForm && (
                      <button
                        onClick={() => setShowPasswordForm(true)}
                        className="text-sm text-sky-600 hover:text-sky-500 font-medium"
                      >
                        Change Password
                      </button>
                    )}
                  </div>

                  {showPasswordForm && (
                    <form onSubmit={handlePasswordChange} className="space-y-3">
                      {passwordError && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          {passwordError}
                        </div>
                      )}

                      {/* Current Password */}
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Current Password</label>
                        <div className="relative">
                          <input
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                            className="w-full px-3 py-2 pr-10 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                            placeholder="Enter current password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                          >
                            {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      {/* New Password */}
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">New Password</label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                            className="w-full px-3 py-2 pr-10 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                            placeholder="Enter new password (min 8 characters)"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                          >
                            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      {/* Confirm New Password */}
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Confirm New Password</label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={passwordData.confirmPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                            className="w-full px-3 py-2 pr-10 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                            placeholder="Confirm new password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 pt-2">
                        <button
                          type="submit"
                          disabled={isChangingPassword}
                          className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg text-sm font-medium hover:bg-sky-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isChangingPassword ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Changing...
                            </>
                          ) : (
                            'Change Password'
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={resetPasswordForm}
                          className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-sm font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          ) : selectedTab === 'branding' ? (
            /* Branding Tab */
            <div className="max-w-4xl mx-auto">
              <div className="space-y-6">
                {/* Agent Information Section */}
                <div className="dark:bg-slate-950/60 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                  <h4 className="text-lg font-semibold dark:text-white text-slate-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-sky-500" />
                    Agent Information
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Agent Photo */}
                    <div className="flex flex-col items-center">
                      <div className="relative group mb-3">
                        <div className="h-24 w-24 rounded-2xl dark:bg-slate-800 bg-slate-200 border-2 dark:border-slate-700 border-slate-300 overflow-hidden flex items-center justify-center">
                          {isUploadingAgentPhoto ? (
                            <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
                          ) : (agentPhotoPreview || brandingSettings.agentPhoto) && !agentPhotoError ? (
                            <img
                              src={agentPhotoPreview || brandingSettings.agentPhoto}
                              alt="Agent"
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                console.error('[AccountSettingsModal] Agent photo failed to load:', brandingSettings.agentPhoto, e);
                                setAgentPhotoError(true);
                              }}
                              onLoad={() => console.log('[AccountSettingsModal] Agent photo loaded successfully')}
                            />
                          ) : (
                            <User className="w-10 h-10 dark:text-slate-500 text-slate-400" />
                          )}
                        </div>
                        <button
                          onClick={() => agentPhotoInputRef.current?.click()}
                          disabled={isUploadingAgentPhoto}
                          className="absolute -bottom-1 -right-1 p-1.5 rounded-lg bg-sky-600 text-white shadow-lg hover:bg-sky-500 transition-colors disabled:opacity-50"
                        >
                          <Camera size={14} />
                        </button>
                        <input
                          type="file"
                          ref={agentPhotoInputRef}
                          onChange={handleAgentPhotoUpload}
                          className="hidden"
                          accept="image/*"
                        />
                      </div>
                      <p className="text-xs text-slate-500">Agent Photo</p>
                    </div>

                    {/* Agent Details */}
                    <div className="md:col-span-2 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Agent Name</label>
                          <input
                            type="text"
                            value={brandingSettings.agentName}
                            onChange={(e) => setBrandingSettings(prev => ({ ...prev, agentName: e.target.value }))}
                            placeholder="John Smith"
                            className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Title</label>
                          <input
                            type="text"
                            value={brandingSettings.agentTitle}
                            onChange={(e) => setBrandingSettings(prev => ({ ...prev, agentTitle: e.target.value }))}
                            placeholder="Real Estate Agent"
                            className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-slate-500 mb-1 flex items-center gap-1">
                            <Phone className="w-3 h-3" /> Phone
                          </label>
                          <input
                            type="tel"
                            value={brandingSettings.agentPhone}
                            onChange={(e) => setBrandingSettings(prev => ({ ...prev, agentPhone: e.target.value }))}
                            placeholder="(555) 123-4567"
                            className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1 flex items-center gap-1">
                            <Mail className="w-3 h-3" /> Email
                          </label>
                          <input
                            type="email"
                            value={brandingSettings.agentEmail}
                            onChange={(e) => setBrandingSettings(prev => ({ ...prev, agentEmail: e.target.value }))}
                            placeholder="john@example.com"
                            className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Brokerage Information Section */}
                <div className="dark:bg-slate-950/60 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                  <h4 className="text-lg font-semibold dark:text-white text-slate-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-sky-500" />
                    Brokerage Information
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Brokerage Logo */}
                    <div className="flex flex-col items-center">
                      <div className="relative group mb-3">
                        <div className="h-24 w-24 rounded-2xl dark:bg-slate-800 bg-slate-200 border-2 dark:border-slate-700 border-slate-300 overflow-hidden flex items-center justify-center">
                          {isUploadingLogo ? (
                            <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
                          ) : (brokerageLogoPreview || brandingSettings.brokerageLogo) && !brokerageLogoError ? (
                            <img
                              src={brokerageLogoPreview || brandingSettings.brokerageLogo}
                              alt="Brokerage Logo"
                              className="h-full w-full object-contain p-2"
                              onError={() => setBrokerageLogoError(true)}
                            />
                          ) : (
                            <Building2 className="w-10 h-10 dark:text-slate-500 text-slate-400" />
                          )}
                        </div>
                        <button
                          onClick={() => brokerageLogoInputRef.current?.click()}
                          disabled={isUploadingLogo}
                          className="absolute -bottom-1 -right-1 p-1.5 rounded-lg bg-sky-600 text-white shadow-lg hover:bg-sky-500 transition-colors disabled:opacity-50"
                        >
                          <Upload size={14} />
                        </button>
                        <input
                          type="file"
                          ref={brokerageLogoInputRef}
                          onChange={handleBrokerageLogoUpload}
                          className="hidden"
                          accept="image/*"
                        />
                      </div>
                      <p className="text-xs text-slate-500">Brokerage Logo</p>
                    </div>

                    {/* Brokerage Name */}
                    <div className="md:col-span-2">
                      <label className="block text-xs text-slate-500 mb-1">Brokerage Name</label>
                      <input
                        type="text"
                        value={brandingSettings.brokerageName}
                        onChange={(e) => setBrandingSettings(prev => ({ ...prev, brokerageName: e.target.value }))}
                        placeholder="ABC Realty"
                        className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Accent Color Section */}
                <div className="dark:bg-slate-950/60 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                  <h4 className="text-lg font-semibold dark:text-white text-slate-900 mb-4 flex items-center gap-2">
                    <Palette className="w-5 h-5 text-sky-500" />
                    Brand Colors
                  </h4>

                  <div className="space-y-4">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Choose an accent color for your property galleries
                    </p>

                    {/* Color Presets */}
                    <div className="flex flex-wrap gap-3">
                      {ACCENT_COLOR_PRESETS.map((preset) => (
                        <button
                          key={preset.id}
                          onClick={() => setBrandingSettings(prev => ({ ...prev, accentColor: preset.color }))}
                          className={`relative group flex flex-col items-center gap-1 p-2 rounded-lg transition-all ${
                            brandingSettings.accentColor === preset.color
                              ? 'ring-2 ring-sky-500 bg-sky-50 dark:bg-sky-900/20'
                              : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                          }`}
                        >
                          <div
                            className="w-10 h-10 rounded-full shadow-md"
                            style={{ backgroundColor: preset.color }}
                          />
                          <span className="text-xs text-slate-600 dark:text-slate-400">{preset.name}</span>
                          {brandingSettings.accentColor === preset.color && (
                            <div className="absolute -top-1 -right-1 bg-sky-500 rounded-full p-0.5">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Custom Color Input */}
                    <div className="flex items-center gap-3">
                      <label className="text-sm text-slate-600 dark:text-slate-400">Custom:</label>
                      <input
                        type="color"
                        value={brandingSettings.accentColor}
                        onChange={(e) => setBrandingSettings(prev => ({ ...prev, accentColor: e.target.value }))}
                        className="w-10 h-10 rounded-lg cursor-pointer border-0"
                      />
                      <input
                        type="text"
                        value={brandingSettings.accentColor}
                        onChange={(e) => setBrandingSettings(prev => ({ ...prev, accentColor: e.target.value }))}
                        placeholder="#d6b27f"
                        className="w-28 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white font-mono"
                      />
                    </div>
                  </div>
                </div>

                {/* Gallery Template Section */}
                <div className="dark:bg-slate-950/60 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                  <h4 className="text-lg font-semibold dark:text-white text-slate-900 mb-4">
                    Default Gallery Template
                  </h4>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                    Choose the default template for your property galleries. You can override this per-gallery.
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-80 overflow-y-auto pr-2">
                    {PROPERTY_GALLERY_TEMPLATES.slice(0, 15).map((template) => (
                      <button
                        key={template.id}
                        onClick={() => setBrandingSettings(prev => ({ ...prev, defaultTemplate: template.id }))}
                        className={`relative rounded-xl overflow-hidden border-2 transition-all ${
                          brandingSettings.defaultTemplate === template.id
                            ? 'border-sky-500 ring-2 ring-sky-500/30'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600'
                        }`}
                      >
                        <div className={`h-16 w-full bg-gradient-to-br ${template.shell}`} />
                        <div className={`absolute bottom-0 left-0 right-0 p-2 ${template.isDark ? 'bg-black/60' : 'bg-white/80'}`}>
                          <p className={`text-xs font-medium truncate ${template.isDark ? 'text-white' : 'text-slate-900'}`}>
                            {template.name}
                          </p>
                        </div>
                        {brandingSettings.defaultTemplate === template.id && (
                          <div className="absolute top-1 right-1 bg-sky-500 rounded-full p-0.5">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleSaveBranding}
                    disabled={isSavingBranding}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-xl font-medium shadow-lg shadow-sky-500/30 hover:from-sky-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSavingBranding ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5" />
                        Save Branding Settings
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : selectedTab === 'phone' ? (
            /* Phone Numbers Tab - Ultra+ only */
            <PhoneNumbersManager />
          ) : (
            /* Subscription Tab */
            <div className="space-y-6">
              <div className="text-center max-w-2xl mx-auto">
                <h3 className="text-2xl font-bold dark:text-white text-slate-900 mb-2">
                  Choose Your Plan
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Upgrade anytime. Downgrade or cancel at any time. All plans include secure Stripe checkout.
                </p>
              </div>

              {/* Tier Cards */}
              <div className="grid md:grid-cols-3 gap-4">
                {SUBSCRIPTION_TIERS.map((tier) => {
                  const Icon = tier.icon;
                  const isCurrentTier = tier.id === currentTier;
                  const canUpgrade = tier.id !== 'free' && tier.id !== currentTier;

                  return (
                    <div
                      key={tier.id}
                      className={`relative rounded-xl border-2 overflow-hidden transition-all ${tier.highlight
                        ? 'border-violet-500 dark:border-violet-400 shadow-xl shadow-violet-500/20'
                        : isCurrentTier
                          ? 'border-sky-500 dark:border-sky-400'
                          : 'border-slate-200 dark:border-slate-700'
                        } ${tier.highlight ? 'transform scale-105' : ''}`}
                    >
                      {/* Highlight Badge */}
                      {tier.highlight && (
                        <div className="absolute top-0 right-0 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                          RECOMMENDED
                        </div>
                      )}

                      {/* Current Plan Badge */}
                      {isCurrentTier && (
                        <div className="absolute top-0 left-0 bg-sky-600 text-white text-xs font-bold px-3 py-1 rounded-br-lg">
                          CURRENT PLAN
                        </div>
                      )}

                      <div className="p-6 dark:bg-slate-900 bg-white">
                        {/* Icon & Name */}
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`p-3 rounded-xl ${tier.color === 'violet'
                            ? 'bg-violet-500/10 text-violet-500'
                            : tier.color === 'sky'
                              ? 'bg-sky-500/10 text-sky-500'
                              : 'bg-slate-500/10 text-slate-500'
                            }`}>
                            <Icon size={24} />
                          </div>
                          <div>
                            <h4 className="font-bold text-lg dark:text-white text-slate-900">
                              {tier.name}
                            </h4>
                            <p className="text-xs text-slate-500">{tier.credits}</p>
                          </div>
                        </div>

                        {/* Price */}
                        <div className="mb-6">
                          <div className="flex items-baseline gap-1">
                            <span className="text-4xl font-bold dark:text-white text-slate-900">
                              {tier.price}
                            </span>
                            <span className="text-slate-500 text-sm">{tier.period}</span>
                          </div>
                        </div>

                        {/* Features */}
                        <ul className="space-y-3 mb-6">
                          {tier.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm">
                              <Check size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                              <span className="dark:text-slate-300 text-slate-700">{feature}</span>
                            </li>
                          ))}
                        </ul>

                        {/* CTA Button */}
                        {isCurrentTier ? (
                          <button
                            type="button"
                            onClick={onOpenBillingPortal}
                            disabled={isPaymentProcessing || tier.id === 'free'}
                            className={`w-full py-3 rounded-lg font-semibold transition-all ${tier.id === 'free'
                              ? 'bg-slate-200 dark:bg-slate-800 text-slate-500 cursor-not-allowed'
                              : 'border-2 border-sky-500 dark:border-sky-400 text-sky-600 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-500/10'
                              }`}
                          >
                            {tier.id === 'free' ? 'Current Plan' : 'Manage Billing'}
                          </button>
                        ) : canUpgrade ? (
                          <button
                            type="button"
                            onClick={() => handleUpgradeTier(tier.id)}
                            disabled={isPaymentProcessing}
                            className={`w-full py-3 rounded-lg font-semibold transition-all hover:scale-105 active:scale-95 ${tier.highlight
                              ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg hover:shadow-xl'
                              : 'bg-sky-600 text-white hover:bg-sky-500'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {isPaymentProcessing ? (
                              <span className="flex items-center justify-center gap-2">
                                <Loader2 size={16} className="animate-spin" />
                                Processing...
                              </span>
                            ) : (
                              `Select ${tier.name}`
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled
                            className="w-full py-3 rounded-lg font-semibold bg-slate-200 dark:bg-slate-800 text-slate-500 cursor-not-allowed"
                          >
                            {tier.id === 'free' ? 'Contact Support to Downgrade' : 'Lower Tier'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          )}
        </div>
      </div>

      {/* Success Popups */}
      {passwordSuccess && (
        <div className="fixed bottom-6 right-6 flex items-center gap-3 px-5 py-4 bg-emerald-600 text-white rounded-xl shadow-2xl shadow-emerald-900/30 z-[200]">
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium">Password changed successfully!</span>
        </div>
      )}
      {brandingSaved && (
        <div className="fixed bottom-6 right-6 flex items-center gap-3 px-5 py-4 bg-emerald-600 text-white rounded-xl shadow-2xl shadow-emerald-900/30 z-[200]">
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium">Branding settings saved!</span>
        </div>
      )}
    </div>
  );
};

export default AccountSettingsModal;
